import { hash } from '@ember/helper';
import Component from '@glimmer/component';
import { or, eq } from 'ember-truth-helpers';
import BvIcon, { type BvIconSignature } from 'volta/components/bv-icon';
import BvSpinner from 'volta/components/bv-spinner';
import BvTooltip from 'volta/components/bv-tooltip';
import classNames from 'volta/helpers/class-names';
import orElse from 'volta/helpers/or-else';

import type { TText } from 'volta/utils/text-utils';
import type { TButtonSize } from '.';
import type { BvTooltipSignature, TTooltipSide, TooltipContent } from 'volta/components/bv-tooltip';

export interface BvBtnContentArgs {
  asyncAction: unknown;
  disclosure?: 'auto' | 'up' | 'down' | boolean;
  iconLeft?: string;
  iconLeftColor?: BvIconSignature['Args']['color'];
  iconRight?: string;
  iconRightColor?: BvIconSignature['Args']['color'];
  isBusy?: boolean;
  loadingProgress?: number;
  pressed?: boolean;
  size?: TButtonSize;
  text?: TText;
  tooltip?: BvTooltipSignature['Args']['content'] | TText;
  tooltipSide?: TTooltipSide;
  tooltipText?: BvTooltipSignature['Args']['text'];
}

export interface BvBtnContentSignature {
  Args: BvBtnContentArgs;
  Blocks: {
    default: [unknown];
  };
}

const ICON_SIZE_MAP: Record<TButtonSize, BvIconSignature['Args']['size']> = {
  xs: 'sm',
  sm: 'sm'
};

export default class BvBtnContentComponent extends Component<BvBtnContentSignature> {
  // Only way to handle polymorphic types
  get tooltipContent() {
    const { tooltip } = this.args;
    if (typeof tooltip === 'string') {
      return undefined;
    }
    return tooltip as TooltipContent[];
  }

  // Only way to handle polymorphic types
  get tooltipText() {
    const { tooltip, tooltipText } = this.args;
    if (typeof tooltip === 'string') {
      return tooltip;
    }
    return tooltipText;
  }

  get iconSize() {
    return this.args.disclosure ? 'sm' : this.args.size ? ICON_SIZE_MAP[this.args.size] : 'md';
  }

  <template>
    {{#if @isBusy}}
      <BvSpinner
        class='bv-btn__spinner'
        @size={{if (eq @size 'xs') 'xs' 'sm'}}
        @value={{@loadingProgress}}
      />
    {{/if}}
    <span class='bv-btn__content'>
      {{#if @iconLeft}}
        <BvIcon
          @glyph={{@iconLeft}}
          @color={{@iconLeftColor}}
          @size={{this.iconSize}}
          class='bv-btn__icon bv-btn__icon-before'
        />
      {{/if}}
      {{#if (has-block)}}
        <span class='bv-btn__text u-ellipsis'>
          {{yield (hash task=@asyncAction pressed=@pressed)}}
        </span>
      {{else if @text}}
        <span class='bv-btn__text u-ellipsis'>
          {{@text}}
        </span>
      {{/if}}
      {{#if (or @iconRight @disclosure)}}
        <BvIcon
          class={{classNames 'bv-btn__icon bv-btn__icon-after'}}
          @glyph={{or
            @iconRight
            (if (eq @disclosure 'up') 'caret-up' (if (eq @disclosure 'down') 'caret-down' 'select'))
          }}
          @size={{this.iconSize}}
          @color={{@iconRightColor}}
        />
      {{/if}}
    </span>
    {{#if (orElse @tooltipText @tooltip)}}
      <BvTooltip
        @content={{this.tooltipContent}}
        @text={{this.tooltipText}}
        @side={{or @tooltipSide 'auto'}}
      />
    {{/if}}
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'BvBtn::Content': typeof BvBtnContentComponent;
    'bv-btn/content': typeof BvBtnContentComponent;
  }
}
